export enum RequestTypes {
  Get = "GET",
  Post = "POST",
  Put = "PUT",
  Patch = "PATCH",
  Delete = "DELETE",
}

export interface ResponseBody<T> extends Body {
  ok: boolean;
  status: number;
  statusText: string;
  json(): Promise<T>;
}

export class HttpClient {
  private readonly apiHost: string;
  private readonly token: string;

  constructor(apiHost: string, token: string) {
    this.apiHost = apiHost;
    this.token = token;
  }

  private defaultHeaders(): Record<string, any> {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `token ${this.token}`,
    };
  }

  private buildRequest(
    requestType: RequestTypes,
    body?: any,
    requestOptions?: Request,
    extraHeaders?: Record<string, string>
  ): Request {
    let request = {} as any;
    let headers = this.defaultHeaders();
    if (extraHeaders) {
      headers = { ...headers, ...extraHeaders };
    }
    if (requestOptions) {
      request = { ...requestOptions };
    }
    request.headers = headers;
    request.method = requestType;
    request.credentials = "include";
    if (body) {
      request.body = JSON.stringify(body);
    }
    return request as Request;
  }

  private makeRequest<T>(uri: string, request: Request): Promise<ResponseBody<T>> {
    const url = `${this.apiHost}${uri}`;
    return fetch(url, request);
  }

  get<T>(
    apiEndpoint: string,
    requestOptions?: Request,
    extraHeaders?: Record<string, string>
  ): Promise<ResponseBody<T>> {
    const request = this.buildRequest(RequestTypes.Get, undefined, requestOptions, extraHeaders);
    return this.makeRequest<T>(apiEndpoint, request);
  }

  post<T>(
    apiEndpoint: string,
    body?: any,
    requestOptions?: Request,
    extraHeaders?: Record<string, string>
  ): Promise<ResponseBody<T>> {
    const request = this.buildRequest(RequestTypes.Post, body, requestOptions, extraHeaders);
    return this.makeRequest<T>(apiEndpoint, request);
  }

  put<T>(
    apiEndpoint: string,
    body: any,
    requestOptions?: Request,
    extraHeaders?: Record<string, string>
  ): Promise<ResponseBody<T>> {
    const request = this.buildRequest(RequestTypes.Put, body, requestOptions, extraHeaders);
    return this.makeRequest<T>(apiEndpoint, request);
  }

  patch<T>(
    apiEndpoint: string,
    body: any,
    requestOptions?: Request,
    extraHeaders?: Record<string, string>
  ): Promise<ResponseBody<T>> {
    const request = this.buildRequest(RequestTypes.Patch, body, requestOptions, extraHeaders);
    return this.makeRequest<T>(apiEndpoint, request);
  }

  del<T>(
    apiEndpoint: string,
    body?: any,
    requestOptions?: Request,
    extraHeaders?: Record<string, string>
  ): Promise<ResponseBody<T>> {
    const request = this.buildRequest(RequestTypes.Delete, body, requestOptions, extraHeaders);
    return this.makeRequest<T>(apiEndpoint, request);
  }
}
